import * as Sentry from '@sentry/react'
import { WemoloTypography } from '../Wemolo'
import React, { FC, ReactNode } from 'react'
import { ErrorBoundary, FallbackProps } from 'react-error-boundary'
import { useHistory } from 'react-router-dom'
import { CustomButton } from '../Button'
import styles from './GlobalErrorBoundary.module.css'

interface GlobalErrorBoundaryProps {
    children: ReactNode
}

interface AuthErrorFallbackProps {
    error?: Error
    logout?: () => void
}

export const AuthErrorFallback: FC<AuthErrorFallbackProps> = ({ error, logout }) => {
    return (
        <div role="alert" className={styles.fallback}>
            <WemoloTypography variant="headline-medium" tag="h1" className={styles.title}>
                Authentication Error
            </WemoloTypography>
            {error && <pre className={styles.errorMessage}>{error.message}</pre>}
            <div>
                Please try to refresh the page and/or login again. If the issue persists, please reach out to AIoT.
            </div>
            {logout && <CustomButton onClick={logout}>Logout</CustomButton>}
        </div>
    )
}
const Fallback = ({ error, resetErrorBoundary }: FallbackProps) => {
    return (
        <div role="alert" className={styles.fallback}>
            <WemoloTypography variant="headline-medium" tag="h1">
                Oops! Something went wrong
            </WemoloTypography>
            <pre className={styles.errorMessage}>{error.message}</pre>
            <CustomButton onClick={resetErrorBoundary}>Go to Homepage</CustomButton>
        </div>
    )
}

const GlobalErrorBoundary = ({ children }: GlobalErrorBoundaryProps) => {
    const history = useHistory()

    const logError = (error: Error, info: { componentStack: string }) => {
        Sentry.captureException(error, {
            extra: { componentStack: info.componentStack }
        })
    }

    return (
        <ErrorBoundary FallbackComponent={Fallback} onError={logError} onReset={() => history.replace('/')}>
            {children}
        </ErrorBoundary>
    )
}

export default GlobalErrorBoundary
